/** Blocs de contenu : titre, texte, image, galerie, colonnes, section éditoriale, sommaire. */
import React from "react";
import { sanitize } from "../../lib/sanitize";
import { CERTITUDE_LABEL, filmStyle, trackMariage } from "./shared";

/** Visite 360° Matterport chargée uniquement après interaction (performance). */
function MatterportLazy({ url, poster, title }) {
  const [loaded, setLoaded] = React.useState(false);
  return (
    <div className="mt-6 relative aspect-video border border-pierre bg-domaine/90 overflow-hidden" data-testid="matterport-lazy">
      {loaded ? (
        <iframe src={url} title={`Visite virtuelle 360° — ${title || "Domaine de la Briouse"}`}
                className="absolute inset-0 w-full h-full" allow="fullscreen; xr-spatial-tracking" loading="lazy" />
      ) : (
        <button type="button" onClick={() => { setLoaded(true); trackMariage("click_360_rigoletto"); }}
                className="absolute inset-0 w-full h-full group" data-testid="matterport-lazy-play">
          {poster && <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-60 transition-opacity" loading="lazy" />}
          <span className="relative z-10 flex flex-col items-center justify-center h-full text-white">
            <span className="w-16 h-16 rounded-full bg-white/90 text-domaine flex items-center justify-center text-2xl shadow-lg group-hover:scale-105 transition-transform">▶</span>
            <span className="mt-3 text-xs uppercase tracking-[0.25em] [text-shadow:0_1px_4px_rgba(0,0,0,0.7)]">Lancer la visite 360°</span>
            <span className="mt-1 text-[11px] text-white/85 [text-shadow:0_1px_4px_rgba(0,0,0,0.7)]">La visite se charge uniquement à votre demande</span>
          </span>
        </button>
      )}
    </div>
  );
}

export function TitleBlock({ d, t }) {
  const H = d.level === 3 ? "h3" : "h2";
  const cls = d.level === 3 ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl";
  return React.createElement(H, { className: `${cls} font-serif text-domaine` }, t(d.text));
}

export function TextBlock({ d, t }) {
  const html = d.html || null;
  if (html) {
    return <div className="prose prose-ardoise max-w-none text-base leading-relaxed"
                 dangerouslySetInnerHTML={{ __html: sanitize(t(html)) }} />;
  }
  return <div className="prose prose-ardoise max-w-none text-base leading-relaxed whitespace-pre-wrap">{t(d.text || "")}</div>;
}

export function ImageBlock({ d, abs }) {
  return d.url ? (
    <figure>
      <img src={abs(d.url)} alt={d.alt || ""} style={filmStyle(d)} className="w-full border border-pierre" loading="lazy" />
      {d.caption && <figcaption className="text-xs italic text-ardoise/60 mt-2 text-center">{d.caption}</figcaption>}
    </figure>
  ) : null;
}

export function GalleryBlock({ d, abs }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {(d.images || []).map((u, i) => (
        <img key={u || `img-${i}`} src={abs(u)} alt="" loading="lazy"
             className="w-full aspect-square object-cover border border-pierre hover:border-or transition-colors" />
      ))}
    </div>
  );
}

export function Columns2Block({ d, t, abs }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
      {d.image_url && <img src={abs(d.image_url)} alt={d.image_alt || ""}
                            className="w-full aspect-[4/3] object-cover border border-pierre" loading="lazy" />}
      <div>
        {d.title && <h3 className="text-2xl md:text-3xl font-serif text-domaine">{t(d.title)}</h3>}
        {d.text_html
          ? <div className="mt-4 prose prose-sm max-w-none text-ardoise" dangerouslySetInnerHTML={{ __html: sanitize(t(d.text_html)) }} />
          : (d.text && <p className="mt-4 text-base text-ardoise whitespace-pre-wrap">{t(d.text)}</p>)
        }
      </div>
    </div>
  );
}

export function CtaButtonBlock({ d }) {
  const href = d.url || d.link || "#";
  const isExternal = /^https?:\/\//.test(href) || d.open_new_tab;
  const linkProps = isExternal
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};
  return (
    <div className={d.align === "left" ? "text-left py-4" : d.align === "right" ? "text-right py-4" : "text-center py-4"}>
      <a href={href} {...linkProps}
         className={`inline-block px-8 py-3 font-medium transition-colors ${d.style === "secondary"
           ? "border border-domaine text-domaine hover:bg-domaine hover:text-lin"
           : "bg-domaine text-lin hover:bg-or"}`}>
        {d.label || "En savoir plus"}
      </a>
    </div>
  );
}

export function SeparatorBlock() {
  return <div className="flex items-center justify-center py-8"><span className="text-or text-2xl tracking-[0.5em]">·  ·  ·</span></div>;
}

export function EditorialSectionBlock({ d, t, abs }) {
  const anchor = (d.anchor || "").replace(/[^a-z0-9-]/gi, "").toLowerCase() || null;
  const pos = d.image_position || "right"; // left | right | full | none
  const number = d.chapter_number || "";
  const eyebrow = d.eyebrow || "";
  const title = t(d.title || "");
  const subtitle = t(d.subtitle || "");
  const html = t(d.text_html || d.text || "");
  const images = (d.images || []).filter(Boolean).map((u) => abs(u));
  const primary = images[0];
  const cta = d.cta_label && d.cta_link ? { label: t(d.cta_label), href: d.cta_link } : null;
  const ctas = Array.isArray(d.ctas) && d.ctas.length > 0
    ? d.ctas.filter((c) => c.label && c.link).map((c) => ({ label: t(c.label), href: c.link }))
    : (cta ? [cta] : []);
  const CtaBtn = (ctas.length > 0 || d.matterport_url) ? (
    <div className="mt-8">
      {ctas.length > 0 && (
      <div className="flex flex-wrap gap-3">
      {ctas.map((c, i) => {
        const isPdf = /\.pdf(?:$|[?#])/i.test(c.href || "") || /\/plaquettes\//.test(c.href || "");
        return (
          <a key={`${anchor}-cta-${i}`} href={c.href}
             target={isPdf ? "_blank" : undefined}
             rel={isPdf ? "noopener noreferrer" : undefined}
             className={i === 0
               ? "inline-block px-8 py-3 bg-domaine text-lin text-xs uppercase tracking-[0.2em] hover:bg-or transition-colors"
               : "inline-block px-8 py-3 border border-domaine text-domaine text-xs uppercase tracking-[0.2em] hover:bg-domaine hover:text-lin transition-colors"}>
            {c.label}
          </a>
        );
      })}
      </div>
      )}
      {d.matterport_url && <MatterportLazy url={d.matterport_url} poster={primary} title={title} />}
    </div>
  ) : null;
  const Head = (
    <header className="mb-8">
      {number && (
        <div className="text-or font-serif text-5xl md:text-6xl leading-none opacity-70 mb-4">{number}</div>
      )}
      {eyebrow && (
        <div className="text-[11px] uppercase tracking-[0.3em] text-or mb-3">{eyebrow}</div>
      )}
      <h2 className="text-3xl md:text-5xl font-serif text-domaine leading-tight">{title}</h2>
      {subtitle && <p className="mt-3 text-lg text-ardoise/80 max-w-2xl">{subtitle}</p>}
      {d.certitude && d.certitude !== "documented" && (
        <span className="inline-block mt-4 text-[9px] uppercase tracking-widest px-2 py-0.5 border border-or/40 text-or/80 bg-or/5">
          {CERTITUDE_LABEL[d.certitude] || d.certitude}
        </span>
      )}
    </header>
  );
  const Body = html ? (
    <div className="prose prose-ardoise max-w-none text-base leading-relaxed"
         dangerouslySetInnerHTML={{ __html: sanitize(html) }} />
  ) : null;

  const matterportUrl = (d.matterport_url || "").trim();
  const MatterportEmbed = matterportUrl ? (
    <div className="aspect-video border border-pierre bg-domaine my-8" data-testid={anchor ? `matterport-${anchor}` : "matterport"}>
      <iframe
        src={matterportUrl}
        title={`Visite virtuelle 360° — ${title || "Domaine de la Briouse"}`}
        allow="xr-spatial-tracking; gyroscope; accelerometer; fullscreen; autoplay"
        allowFullScreen
        frameBorder="0"
        loading="lazy"
        className="w-full h-full"
      />
    </div>
  ) : null;

  let inner;
  if (pos === "full" && primary) {
    inner = (
      <div>
        {Head}
        {MatterportEmbed}
        <img src={primary} alt={d.image_alt || title}
             loading="lazy" style={filmStyle(d)}
             className="w-full aspect-[21/9] object-cover border border-pierre my-8" />
        {Body}
        {images.length > 1 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-8">
            {images.slice(1).map((u, i) => (
              <img key={`${anchor}-g-${i}`} src={u} alt="" loading="lazy" style={filmStyle(d)}
                   className="w-full aspect-[4/3] object-cover border border-pierre" />
            ))}
          </div>
        )}
        {CtaBtn}
      </div>
    );
  } else if (pos === "none" || !primary) {
    inner = (
      <div className={matterportUrl ? "max-w-6xl mx-auto" : "max-w-3xl"}>
        {Head}
        {MatterportEmbed}
        {Body}
        {images.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-8">
            {images.map((u, i) => (
              <img key={`${anchor}-g-${i}`} src={u} alt="" loading="lazy" style={filmStyle(d)}
                   className="w-full aspect-[4/3] object-cover border border-pierre" />
            ))}
          </div>
        )}
        {CtaBtn}
      </div>
    );
  } else {
    const imageBlock = (
      <div className="relative">
        <img src={primary} alt={d.image_alt || title}
             loading="lazy" style={filmStyle(d)}
             className="w-full aspect-[4/5] object-cover border border-pierre" />
        {images.length > 1 && (
          <div className="grid grid-cols-3 gap-2 mt-2">
            {images.slice(1, 4).map((u, i) => (
              <img key={`${anchor}-t-${i}`} src={u} alt="" loading="lazy" style={filmStyle(d)}
                   className="w-full aspect-square object-cover border border-pierre" />
            ))}
          </div>
        )}
      </div>
    );
    const textBlock = (
      <div>
        {Head}
        {MatterportEmbed}
        {Body}
        {CtaBtn}
      </div>
    );
    inner = (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
        {pos === "left" ? <>{imageBlock}{textBlock}</> : <>{textBlock}{imageBlock}</>}
      </div>
    );
  }
  // Réduit le padding vertical si le bloc est purement titre (pas d'image ni de contenu HTML)
  const isHeaderOnly = !html && images.length === 0;
  const sectionPad = isHeaderOnly ? "pt-16 md:pt-20 pb-4 md:pb-6" : "py-16 md:py-24";
  return (
    <section id={anchor || undefined}
             data-testid={anchor ? `editorial-${anchor}` : undefined}
             style={{ scrollMarginTop: "140px" }}
             className={`${sectionPad} border-t border-pierre/60 first:border-t-0`}>
      {inner}
    </section>
  );
}

export function AnchorNavBlock({ d, t }) {
  const items = d.items || [];
  return (
    <nav className="py-6 border-y border-pierre/60 my-8 flex flex-wrap items-center justify-center gap-3 md:gap-8 text-[11px] uppercase tracking-[0.25em]"
         aria-label="Sommaire de la page">
      {items.map((it, i) => (
        <React.Fragment key={`anchor-${i}-${it.anchor}`}>
          {i > 0 && <span className="text-or opacity-40">·</span>}
          <a href={`#${it.anchor}`}
             className="text-ardoise hover:text-domaine transition-colors">
            {t(it.label)}
          </a>
        </React.Fragment>
      ))}
    </nav>
  );
}
